import { Helmet } from "react-helmet-async";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ArrowLeft } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface LegalSection {
  id: string;
  title: string;
}

interface LegalLayoutProps {
  title: string;
  lastUpdate: string;
  sections: LegalSection[];
  children: React.ReactNode;
  canonical: string;
  description: string;
}

export function LegalLayout({ 
  title, 
  lastUpdate, 
  sections, 
  children,
  canonical,
  description
}: LegalLayoutProps) {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: "smooth"
      });
      setIsAccordionOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9F5] py-12 md:py-20 px-4">
      <Helmet>
        <title>{title} | Lista de Fornecedores Gopage</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="container mx-auto max-w-6xl">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar Desktop */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-6">Índice</h4>
              <nav className="flex flex-col space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      "text-left text-sm py-2 px-3 rounded-lg transition-all border-l-2",
                      activeSection === section.id
                        ? "border-primary bg-white font-semibold text-brand-dark shadow-sm"
                        : "border-transparent text-muted-foreground hover:text-brand-dark hover:bg-white/50"
                    )}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Mobile Accordion */}
          <div className="lg:hidden sticky top-4 z-40 mb-8">
            <button
              onClick={() => setIsAccordionOpen(!isAccordionOpen)}
              className="w-full bg-white border border-border p-4 rounded-xl shadow-sm flex items-center justify-between font-semibold"
            >
              Ir para a seção
              <ChevronDown className={cn("w-5 h-5 transition-transform", isAccordionOpen && "rotate-180")} />
            </button>
            {isAccordionOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-border rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="w-full text-left p-4 hover:bg-secondary border-b border-border last:border-0 text-sm"
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Content Area */}
          <main className="flex-1 max-w-[760px] mx-auto lg:mx-0">
            <div className="bg-white rounded-[16px] border border-border p-8 md:p-12 shadow-sm">
              <h1 className="text-3xl md:text-4xl font-bold text-brand-dark mb-2">{title}</h1>
              <p className="text-muted-foreground text-sm mb-12">Última atualização: {lastUpdate}</p>
              
              <div className="legal-content prose prose-slate max-w-none prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h3:text-[19px] prose-h3:mt-8 prose-h3:mb-4 prose-p:text-[16px] prose-p:leading-[1.7] prose-li:text-[16px] prose-li:leading-[1.7] prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-border prose-th:bg-secondary prose-th:p-3 prose-td:border prose-td:border-border prose-td:p-3 overflow-x-auto">
                {children}
              </div>

              <div className="mt-20 pt-8 border-t border-border flex justify-between items-center">
                <Link 
                  to="/" 
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-brand-dark transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar ao início
                </Link>
                <button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="text-sm font-semibold text-primary hover:underline"
                >
                  Topo da página
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
